<template>
  <v-stepper :value="activeStep" class="rever-stepper">
    <v-stepper-header
      ref="stepperContainer"
      style="max-height: 60px"
      class="my-0 py-0 align-center flex-nowrap overflow-scroll hide-scrollbar"
    >
      <template v-for="(step, id, index) in steps">
        <v-stepper-step
          v-ripple="false"
          class="my-0 py-0 px-4"
          :class="
            phases[index].disabled
              ? 'rev-stepper-step-disabled'
              : 'rev-stepper-step-default cursor-pointer'
          "
          edit-icon="mdi-alert-decagram"
          error-icon="!"
          :editable="step === phases.length && hasRequestValidation"
          :step="step"
          :key="id"
          :complete="
            phases[index].complete ||
            (hasRequestValidation && step === phases.length)
          "
          :rules="[
            () => !(hasCompleteError && phases[index].missingFields),
            () => !(showImplementError && index === phases.length - 2),
          ]"
          @click.native="handleStepClick(step, phases[index], index)"
        >
          <div class="d-flex flex-column">
            <p
              class="body-2 ma-0 pa-0 blue-grey--text"
              :class="{
                'text--darken-3': !phases[index].disabled,
              }"
            >
              {{ phases[index].name }}
            </p>

            <p
              v-if="phases[index]?.optional"
              class="caption blue-grey--text text--darken-1 ma-0 pa-0 text-style"
            >
              {{ $t('common.optional') }}
            </p>
            <p
              v-if="showImplementError && index === phases.length - 2"
              class="caption red--text ma-0 pa-0 text-style"
            >
              {{ $t('common.complete') }}
            </p>
            <template v-else>
              <p
                v-if="hasCompleteError && phases[index].missingFields"
                class="caption red--text ma-0 pa-0 text-style"
              >
                {{ phases[index].errorTitle || $t('rev.missing_fields_title') }}
              </p>
            </template>

            <p
              v-if="step === phases.length && hasRequestValidation"
              class="caption yellow--text text--darken-1 ma-0 pa-0 text-style"
            >
              {{ $t('common.requested') }}
            </p>
          </div>
        </v-stepper-step>

        <v-progress-linear
          v-if="step < phases.length"
          background-color="blue-grey lighten-2"
          color="blue"
          class="rounded"
          :style="{
            'min-width': '35px',
            'transition-duration': '0s',
            'max-width': $vuetify.breakpoint.lgAndDown ? '100px' : '300px',
          }"
          :value="getProgressValue(index)"
          :key="step"
        />
      </template>
    </v-stepper-header>
  </v-stepper>
</template>

<script>
const SCROLL_STEP_SIZE = 100

export default {
  name: 'Stepper',

  props: {
    phases: Array,
    steps: Object,
    hasCompleteError: Boolean,
    activeStep: Number,
    hasRequestValidation: Boolean,
    showImplementError: Boolean,
  },

  methods: {
    getProgressValue(index) {
      return this.activeStep > index + 1 ? 100 : 0
    },

    scrollStepper(clickedIndex) {
      const midPoint = Math.floor(Object.keys(this.steps).length / 2)
      const scrollStepSize =
        clickedIndex < midPoint ? -SCROLL_STEP_SIZE : SCROLL_STEP_SIZE

      this.$refs.stepperContainer.scrollBy({
        left: scrollStepSize,
        behavior: 'smooth',
      })
    },

    handleStepClick(step, phase, index) {
      if (phase.disabled) return
      this.$emit('click', step, phase)
      this.scrollStepper(index)
    },
  },
}
</script>
<style lang="scss">
.overflow-scroll {
  overflow: auto;
}

.text-style {
  opacity: 0.6;
  white-space: nowrap;
  text-shadow: none !important;
}
.rever-stepper {
  max-height: 60px !important;

  .v-stepper__label {
    display: flex !important;
  }

  .v-stepper__step__step {
    height: 20px;
    width: 20px;
    min-width: 20px;
  }

  .v-stepper__step--inactive .v-stepper__step__step {
    color: $color-blue !important;
    background-color: transparent !important;
    border: 1px solid $color-blue !important;
    .v-icon {
      color: $color-blue !important;
    }
  }

  .v-stepper__step:not(.v-stepper__step--active):not(
      .v-stepper__step--error
    ):not(.v-stepper__step--editable).v-stepper__step--complete {
    .v-stepper__step__step {
      color: $color-blue !important;
      background-color: transparent !important;
      border: 1px solid $color-blue !important;
      .v-icon {
        color: $color-green !important;
      }
    }
  }

  .v-stepper__step--error {
    .v-stepper__step__step {
      display: flex;
      align-content: center;
      color: $color-red !important;
      background-color: transparent !important;
      border: 1px solid $color-red !important;

      .v-icon {
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 12px;
        color: $color-red !important;
        margin-bottom: 3px !important;
        margin-right: 1px !important;
      }
    }
  }

  .v-stepper__step.v-stepper__step--active.v-stepper__step--error {
    .v-stepper__step__step {
      background-color: $color-red !important;
      .v-icon {
        color: $color-white !important;
      }
    }
  }

  .v-stepper__step.v-stepper__step--active.v-stepper__step--editable.v-stepper__step--complete {
    .v-stepper__step__step {
      background-color: $color-yellow !important;
      .v-icon {
        color: $color-white !important;
      }
    }
  }

  .v-stepper__step--editable:hover {
    background-color: transparent !important;
  }

  .v-stepper__step--editable {
    .v-stepper__step__step {
      color: $color-yellow !important;
      background-color: transparent !important;
      border: 1px solid $color-yellow !important;
      .v-icon {
        font-size: 16px !important;
        color: $color-yellow !important;
      }
    }
  }

  .v-stepper__step--inactive .v-stepper__step--editable {
    .v-stepper__step__step {
      display: flex;
      align-content: center;
      color: $color-yellow !important;
      background-color: transparent !important;
      border: 1px solid $color-yellow !important;
      .v-icon {
        font-size: 16px !important;
        color: $color-yellow !important;
      }
    }
  }
}

//DISABLED STATUS
.rev-stepper-step-disabled.v-stepper__step--inactive {
  .v-stepper__step__step {
    color: $color-blue-grey !important;
    background-color: transparent !important;
    border: 1px solid $color-blue-grey !important;
  }
}

//DEFAULT STATUS
.rev-stepper-step-default.v-stepper__step:not(.v-stepper__step--active):not(
    .v-stepper__step--complete
  ):not(.v-stepper__step--error):not(.v-stepper__step--editable) {
  .v-stepper__step__step {
    color: $color-blue !important;
    background-color: transparent !important;
    border: 1px solid $color-blue !important;
  }
}
</style>
